import React from 'react';
import { Container, Typography, Paper, Box, Grid, Divider, Link } from '@mui/material';
import { DATA_INTELLIGENZ_URL } from '../constants/brand';

function AboutPage() {
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" gutterBottom color="primary" align="center" sx={{ mb: 3 }}>
          Acerca de KeplerApprovalBoard
        </Typography>
        
        <Typography variant="h6" gutterBottom color="secondary">
          ¿Qué es KeplerApprovalBoard?
        </Typography>
        
        <Typography paragraph>
          KeplerApprovalBoard es una aplicación web que sirve como puente entre el ERP Kepler y ClickUp, 
          permitiendo a los usuarios autorizados visualizar y gestionar sus tareas de ClickUp relacionadas 
          con órdenes de servicio usando sus credenciales de Kepler. La aplicación ofrece una interfaz 
          simplificada y centralizada que mejora la experiencia de usuario al eliminar la necesidad de 
          gestionar múltiples credenciales y cambiar constantemente entre plataformas.
        </Typography>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom color="secondary">
          Propósito Principal
        </Typography>
        
        <Typography paragraph>
          El propósito fundamental de KeplerApprovalBoard es brindar una interfaz segura donde el administrador 
          de proyectos por parte del cliente puede realizar autorizaciones de órdenes de servicio de manera 
          eficiente y transparente. El sistema está diseñado para:
        </Typography>
        
        <Box sx={{ pl: 2, mb: 2, borderLeft: '4px solid', borderLeftColor: 'secondary.main' }}>
          <Typography paragraph>
            • <strong>Facilitar autorizaciones documentadas:</strong> Mantener una evidencia clara y verificable 
            de qué documentos fueron autorizados, cuándo y por quién, utilizando hashes de verificación.
          </Typography>
          
          <Typography paragraph>
            • <strong>Proporcionar seguimiento integral:</strong> Permitir a los administradores dar seguimiento 
            al estatus de cada una de las órdenes que han autorizado, visualizando su progreso en tiempo real.
          </Typography>
          
          <Typography paragraph>
            • <strong>Ofrecer visibilidad completa:</strong> Mostrar el estatus integral del proyecto con Kepler, 
            facilitando la toma de decisiones basada en información actualizada y confiable.
          </Typography>
          
          <Typography paragraph>
            • <strong>Garantizar seguridad en el proceso:</strong> Implementar un sistema de autenticación robusto 
            que aproveche las credenciales existentes de Kepler, eliminando la necesidad de gestionar múltiples 
            cuentas de usuario.
          </Typography>
        </Box>
        
        <Typography paragraph>
          Esta aplicación representa una solución especializada que responde a la necesidad crítica de las empresas 
          de mantener procesos de autorización documentados, seguros y fácilmente auditables, sin sacrificar la 
          eficiencia operativa.
        </Typography>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom color="secondary">
          Objetivos del Proyecto
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default', height: '100%' }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Unificación de Acceso
              </Typography>
              <Typography variant="body2">
                Permitir a los usuarios acceder a sus tareas de ClickUp utilizando sus credenciales existentes de Kepler.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default', height: '100%' }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Visualización Centralizada
              </Typography>
              <Typography variant="body2">
                Proporcionar una interfaz clara y organizada para ver el estado de las tareas agrupadas por lista y estado.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default', height: '100%' }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Optimización del Flujo de Trabajo
              </Typography>
              <Typography variant="body2">
                Reducir el tiempo y esfuerzo necesario para gestionar tareas entre diferentes sistemas.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default', height: '100%' }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Seguridad de Datos
              </Typography>
              <Typography variant="body2">
                Mantener un alto nivel de seguridad aprovechando la infraestructura de autenticación de Kepler.
              </Typography>
            </Box>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom color="secondary">
          Tecnologías Utilizadas
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Frontend
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
              <li>React (create-react-app)</li>
              <li>Material UI</li>
              <li>React Router</li>
              <li>Axios para peticiones HTTP</li>
              <li>Context API para gestión de estado</li>
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Backend
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
              <li>Node.js con Express</li>
              <li>Express Session para gestión de sesiones</li>
              <li>Módulo SOAP para comunicación con Kepler</li>
              <li>XML2JS para procesamiento de configuraciones</li>
              <li>Nodemailer para envío de correos</li>
            </Typography>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="h6" gutterBottom color="secondary">
          Caso de Estudio: Integración Tecnológica
        </Typography>
        
        <Typography paragraph>
          KeplerApprovalBoard es una muestra ejemplar de cómo se pueden combinar diferentes tecnologías, 
          incluyendo el ERP Kepler, para automatizar procesos empresariales críticos. Esta aplicación 
          demuestra la capacidad de crear soluciones modernas que se integran con sistemas empresariales 
          existentes, ampliando su funcionalidad sin requerir cambios en sus bases.
        </Typography>
        
        <Typography paragraph>
          Esta plataforma es solo una muestra de las posibilidades que ofrece la integración tecnológica 
          para las empresas que buscan optimizar sus operaciones. Al conectar sistemas tradicionalmente 
          aislados como un ERP (Kepler) y una herramienta de gestión de proyectos (ClickUp), se abren 
          nuevas oportunidades para la automatización, la eficiencia operativa y la mejora en la 
          experiencia de usuario.
        </Typography>
        
        <Typography paragraph>
          Las empresas pueden aprovechar enfoques similares para crear soluciones a medida que respondan 
          a sus necesidades específicas, utilizando el ERP Kepler como base sólida e integrándolo con 
          otras tecnologías para ampliar sus capacidades más allá de sus funciones tradicionales.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom color="secondary">
          Desarrollado por Data Intelligenz
        </Typography>

        <Typography paragraph>
          KeplerApprovalBoard fue desarrollado por{' '}
          <Link
            href={DATA_INTELLIGENZ_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            color="secondary"
            fontWeight="bold"
          >
            Data Intelligenz
          </Link>
          , especialistas en integración de sistemas empresariales y soluciones a medida sobre el ERP Kepler.
          Visite{' '}
          <Link
            href={DATA_INTELLIGENZ_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            color="secondary"
          >
            dataintelligenz.net
          </Link>
          {' '}para conocer más sobre nuestros servicios y casos de éxito.
        </Typography>
      </Paper>
    </Container>
  );
}

export default AboutPage; 